'use client';

import { useCallback, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { useReportWebVitals } from 'next/web-vitals';
import { normalizeTelemetryRoute, type TelemetryEvent, type WebVitalName } from '@/telemetry/events';

type ReportWebVitals = Parameters<typeof useReportWebVitals>[0];

function errorClassName(value: unknown, fallback: string): string {
    const candidate = value instanceof Error
        ? value.name
        : typeof value === 'object' && value !== null && 'name' in value
            ? String(value.name)
            : fallback;
    return /^[A-Za-z][A-Za-z0-9._-]{0,63}$/.test(candidate) ? candidate : fallback;
}

function sendTelemetry(event: TelemetryEvent) {
    if (process.env.NEXT_PUBLIC_TELEMETRY_DISABLED === '1') return;
    const body = JSON.stringify(event);

    if (typeof navigator.sendBeacon === 'function') {
        const accepted = navigator.sendBeacon('/api/telemetry', new Blob([body], { type: 'application/json' }));
        if (accepted) return;
    }

    void fetch('/api/telemetry', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body,
        keepalive: true,
        credentials: 'omit',
    }).catch(() => undefined);
}

export default function WebTelemetry() {
    const pathname = usePathname();
    const reportWebVitals: ReportWebVitals = useCallback((metric) => {
        sendTelemetry({
            schemaVersion: 1,
            type: 'web_vital',
            name: metric.name as WebVitalName,
            route: normalizeTelemetryRoute(window.location.pathname),
            value: metric.value,
            rating: metric.rating,
        });
    }, []);

    useReportWebVitals(reportWebVitals);

    useEffect(() => {
        sendTelemetry({
            schemaVersion: 1,
            type: 'page_view',
            name: 'page_view',
            route: normalizeTelemetryRoute(pathname),
        });
    }, [pathname]);

    useEffect(() => {
        const onError = (event: ErrorEvent) => {
            sendTelemetry({
                schemaVersion: 1,
                type: 'client_error',
                name: errorClassName(event.error, 'ErrorEvent'),
                route: normalizeTelemetryRoute(window.location.pathname),
            });
        };
        const onUnhandledRejection = (event: PromiseRejectionEvent) => {
            sendTelemetry({
                schemaVersion: 1,
                type: 'client_error',
                name: errorClassName(event.reason, 'UnhandledRejection'),
                route: normalizeTelemetryRoute(window.location.pathname),
            });
        };

        window.addEventListener('error', onError);
        window.addEventListener('unhandledrejection', onUnhandledRejection);
        return () => {
            window.removeEventListener('error', onError);
            window.removeEventListener('unhandledrejection', onUnhandledRejection);
        };
    }, []);

    return null;
}
